// Slice 13 — Integrations Hub (CRM connectors + Open API keys + Tickets).
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Loader2, RefreshCw, Plug, KeyRound, Plus, Trash2, Eye, EyeOff,
  CheckCircle2, AlertCircle, Copy, Activity, Ticket as TicketI,
  Clock, Flame, Inbox, MessageSquare, Send,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const TABS = [
  { id: "crm",     label: "CRM Connectors",  icon: Plug },
  { id: "api",     label: "Open API keys",   icon: KeyRound },
  { id: "tickets", label: "Tickets",         icon: TicketI },
  { id: "log",     label: "Sync Log",        icon: Activity },
];

export default function IntegrationsHubPage() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab");
  const [tab, setTab] = useState(TABS.find((t) => t.id === initial)?.id || "crm");
  function switchTab(id) {
    setTab(id);
    setParams(id === "crm" ? {} : { tab: id }, { replace: true });
  }
  return (
    <div className="px-4 sm:px-6 py-6 max-w-6xl" data-testid="integrations-hub">
      <div className="mb-5">
        <h1 className="font-display text-2xl font-bold tracking-tight">Integrations & Tickets</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Connect Connect7 to your CRM, expose a partner Open API, and manage support tickets.
        </p>
      </div>
      <div className="flex gap-1 border-b border-border mb-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => switchTab(t.id)}
              data-testid={`hub-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-all
                ${tab === t.id ? "border-primary text-primary"
                                : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              <Icon className="w-4 h-4"/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "crm"     && <CRMTab />}
      {tab === "api"     && <APIKeysTab />}
      {tab === "tickets" && <TicketsTab />}
      {tab === "log"     && <SyncLogTab />}
    </div>
  );
}


/* ===================== CRM TAB ===================== */
function CRMTab() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null); // connector being edited
  const [testing, setTesting] = useState(null); // {connector_id, ...result}

  async function load() {
    setBusy(true);
    try { const r = await api.get("/integrations/crm"); setRows(r.data.connectors || []); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);

  async function disconnect(id) {
    if (!window.confirm(`Disconnect ${id}?`)) return;
    try { await api.delete(`/integrations/crm/${id}`); load(); toast.success("Disconnected"); }
    catch (e) { toast.error(formatErr(e)); }
  }

  async function test(id) {
    setTesting({ connector_id: id, loading: true });
    try {
      const r = await api.post(`/integrations/crm/${id}/test`);
      setTesting({ connector_id: id, ...r.data });
    } catch (e) { toast.error(formatErr(e)); setTesting(null); }
  }

  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-muted-foreground">{rows.filter((r) => r.configured).length} of {rows.length} CRMs configured</p>
        <Button variant="outline" onClick={load} disabled={busy} className="gap-1.5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>} Refresh
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="crm-grid">
        {rows.map((c) => (
          <Card key={c.id} className="p-4 flex flex-col gap-2" data-testid={`crm-card-${c.id}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold">{c.label}</h3>
              {c.configured ? (
                <Badge variant="outline" className={c.enabled
                  ? "text-[10px] bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-200"
                  : "text-[10px] bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-200"}>
                  {c.enabled ? "active" : "paused"}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px]">not set</Badge>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground">auth: {c.auth}</div>
            {c.api_key_masked && <div className="text-[10px] font-mono text-muted-foreground">{c.api_key_masked}</div>}
            {c.last_synced_at && <div className="text-[10px] text-muted-foreground">synced {c.last_synced_at.slice(0, 16).replace("T", " ")}</div>}
            <div className="flex gap-2 mt-auto pt-2">
              <Button size="sm" variant="outline" className="flex-1 h-7 text-xs"
                onClick={() => setEdit(c)} data-testid={`crm-edit-${c.id}`}>
                {c.configured ? "Edit" : "Configure"}
              </Button>
              {c.configured && (
                <>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => test(c.id)} data-testid={`crm-test-${c.id}`}>
                    Test
                  </Button>
                  <button onClick={() => disconnect(c.id)} className="p-1.5 text-muted-foreground hover:text-red-600" data-testid={`crm-disconnect-${c.id}`}>
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Edit dialog */}
      {edit && (
        <CRMEditDialog
          connector={edit}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); load(); }}
        />
      )}
      {/* Test result dialog */}
      <Dialog open={!!testing} onOpenChange={(o) => !o && setTesting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Connection test · {testing?.connector_id}</DialogTitle></DialogHeader>
          {testing?.loading && <Loader2 className="w-5 h-5 animate-spin mx-auto"/>}
          {testing && !testing.loading && (
            <div className="space-y-2">
              {testing.ok ? (
                <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="w-5 h-5"/>
                  <span className="font-medium">Connected ({testing.latency_ms} ms)</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                  <AlertCircle className="w-5 h-5"/>
                  <span className="font-medium">{testing.error}</span>
                </div>
              )}
              {testing.user && <div className="text-sm">user: <span className="font-mono">{testing.user}</span></div>}
              {testing.scopes?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {testing.scopes.map((s) => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
                </div>
              )}
              {testing.is_mocked && <Badge variant="outline" className="text-[10px] bg-amber-50">simulator</Badge>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function CRMEditDialog({ connector, onClose, onSaved }) {
  const [apiBase, setApiBase] = useState(connector.api_base || "");
  const [apiKey, setApiKey] = useState("");
  const [enabled, setEnabled] = useState(connector.enabled);
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const { canWrite } = useCurrentModulePerms();

  async function save() {
    setBusy(true);
    try {
      const payload = { api_base: apiBase, enabled };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      await api.put(`/integrations/crm/${connector.id}`, payload);
      toast.success(`${connector.label} saved`);
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="crm-edit-dialog">
        <DialogHeader><DialogTitle>{connector.label} · {connector.configured ? "Edit" : "Configure"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>API Base URL (optional)</Label>
            <Input value={apiBase} onChange={(e) => setApiBase(e.target.value)}
              placeholder={`https://api.${connector.id}.com`} data-testid="crm-api-base"/>
          </div>
          <div>
            <Label>API Key</Label>
            <div className="flex gap-2">
              <Input type={showKey ? "text" : "password"} value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={connector.api_key_masked || "Paste API key"}
                className="font-mono" data-testid="crm-api-key"/>
              <Button variant="outline" type="button" onClick={() => setShowKey(!showKey)}>
                {showKey ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </Button>
            </div>
            {connector.api_key_masked && (
              <p className="text-xs text-muted-foreground mt-1">Stored: <span className="font-mono">{connector.api_key_masked}</span> — leave blank to keep.</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="crm-enabled"/>
            <Label>Enabled</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {canWrite && <Button onClick={save} disabled={busy} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="crm-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Save"}
          </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ===================== API KEYS TAB ===================== */
function APIKeysTab() {
  const [rows, setRows] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [limit, setLimit] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const { canWrite, canDelete } = useCurrentModulePerms();

  async function load() {
    try {
      const r = await api.get("/integrations/api-keys");
      setRows(r.data.keys || []);
      setScopes(r.data.available_scopes || []);
      setLimit(r.data.rate_limit);
    } catch (e) { toast.error(formatErr(e)); }
  }
  useEffect(() => { load(); }, []);

  async function revoke(id) {
    if (!window.confirm("Revoke this key? Partners using it will break immediately.")) return;
    try { await api.delete(`/integrations/api-keys/${id}`); load(); toast.success("Revoked"); }
    catch (e) { toast.error(formatErr(e)); }
  }

  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-muted-foreground">
          {rows.filter((r) => !r.is_revoked).length} active key{rows.filter((r) => !r.is_revoked).length === 1 ? "" : "s"}
          {limit && <span> · rate limit: {limit.per_window}/{limit.window_sec}s per key</span>}
        </div>
        {canWrite && <Button onClick={() => setCreating(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5" data-testid="key-new">
          <Plus className="w-4 h-4"/> New API key
        </Button>}
      </div>
      <Card className="overflow-hidden" data-testid="keys-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Name</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Prefix</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Scopes</th>
              <th className="text-right px-3 py-2 text-xs uppercase tracking-wider">Calls</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Last used</th>
              <th className="text-center px-3 py-2 text-xs uppercase tracking-wider">Status</th>
              <th className="text-right px-3 py-2 text-xs uppercase tracking-wider"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground italic">No API keys yet.</td></tr>
            )}
            {rows.map((k) => (
              <tr key={k.id} className="border-t border-border/60 hover:bg-muted/20" data-testid={`key-row-${k.id}`}>
                <td className="px-3 py-2 font-medium">{k.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{k.prefix}…{k.last4}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(k.scopes || []).map((s) => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-mono">{k.request_count || 0}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{k.last_used_at?.slice(0, 16).replace("T", " ") || "—"}</td>
                <td className="px-3 py-2 text-center">
                  {k.is_revoked ? <Badge variant="outline" className="text-[10px] bg-red-50 dark:bg-red-500/20 text-red-700">revoked</Badge>
                                : <Badge variant="outline" className="text-[10px] bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700">active</Badge>}
                </td>
                <td className="px-3 py-2 text-right">
                  {!k.is_revoked && canDelete && (
                    <button onClick={() => revoke(k.id)} className="text-muted-foreground hover:text-red-600 p-1" data-testid={`key-revoke-${k.id}`}>
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Create dialog */}
      {creating && (
        <CreateAPIKeyDialog
          scopes={scopes}
          onClose={() => setCreating(false)}
          onCreated={(k) => { setCreating(false); setNewKey(k); load(); }}
        />
      )}

      {/* One-time reveal dialog */}
      <Dialog open={!!newKey} onOpenChange={(o) => !o && setNewKey(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Save this key — shown once</DialogTitle></DialogHeader>
          {newKey && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Copy now — you won't see this again. Store it in your partner's secret manager.</p>
              <div className="flex gap-2">
                <Input readOnly value={newKey.raw_key} className="font-mono text-xs" data-testid="key-reveal-input"/>
                <Button
                  variant="outline"
                  onClick={() => { navigator.clipboard.writeText(newKey.raw_key); toast.success("Copied"); }}
                  data-testid="key-reveal-copy"
                >
                  <Copy className="w-4 h-4"/>
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {(newKey.scopes || []).map((s) => <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>)}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={() => setNewKey(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CreateAPIKeyDialog({ scopes, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  async function save() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/integrations/api-keys", { name: name.trim(), scopes: picked });
      onCreated(r.data);
      toast.success("API key created");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>New API key</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Partner App — Zapier" data-testid="key-name"/>
          </div>
          <div>
            <Label>Scopes</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {scopes.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setPicked(picked.includes(s) ? picked.filter((x) => x !== s) : [...picked, s])}
                  data-testid={`key-scope-${s.replace(":", "-")}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-all
                    ${picked.includes(s) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"}`}
                >{s}</button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !name.trim() || !picked.length} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="key-create">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Create key"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ===================== TICKETS TAB ===================== */
const TICKET_STATUSES = ["open", "in_progress", "waiting_customer", "resolved", "closed"];
const TICKET_PRIORITIES = ["low", "medium", "high", "urgent"];
const STATUS_STYLES = {
  open:              "bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-200",
  in_progress:       "bg-violet-50 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300 border-violet-200",
  waiting_customer:  "bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-200",
  resolved:          "bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-200",
  closed:            "bg-muted text-muted-foreground border-border",
};
const PRIORITY_STYLES = {
  low:    "bg-slate-50 dark:bg-slate-500/20 text-slate-600 dark:text-slate-300 border-slate-200",
  medium: "bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-200",
  high:   "bg-orange-50 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-200",
  urgent: "bg-red-50 dark:bg-red-500/20 text-red-700 dark:text-red-300 border-red-200",
};

function TicketsTab() {
  const [overview, setOverview] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);

  async function load() {
    setBusy(true);
    try {
      const [o, t] = await Promise.all([
        api.get("/tickets/overview"),
        api.get("/tickets", { params: filter === "all" ? {} : { status: filter } }),
      ]);
      setOverview(o.data);
      setRows(t.data.rows || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); /* eslint-disable-line */ }, [filter]);

  const counts = overview?.counts || {};
  const { canWrite } = useCurrentModulePerms();

  return (
    <>
      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="ticket-tiles">
        <Tile label="Open"          value={counts.open || 0}             icon={Inbox} tone="blue" />
        <Tile label="In progress"   value={counts.in_progress || 0}      icon={Activity} tone="violet"/>
        <Tile label="Waiting"       value={counts.waiting_customer || 0} icon={Clock} tone="amber"/>
        <Tile label="Resolved"      value={counts.resolved || 0}         icon={CheckCircle2} tone="emerald"/>
        <Tile label="SLA breached"  value={overview?.breached || 0}      icon={Flame} tone="red"/>
      </div>

      <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
        <div className="flex flex-wrap gap-1.5">
          {["all", ...TICKET_STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`ticket-filter-${s}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-all
                ${filter === s ? "bg-primary text-primary-foreground border-primary"
                               : "bg-card border-border hover:border-primary/40"}`}
            >{s.replace("_", " ")}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>} Refresh
          </Button>
          {canWrite && <Button onClick={() => setCreating(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5" data-testid="ticket-new">
            <Plus className="w-4 h-4"/> New ticket
          </Button>}
        </div>
      </div>

      <Card className="overflow-hidden" data-testid="tickets-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Subject</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Status</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Priority</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Contact</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Assignee</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">SLA</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Updated</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground italic">
                No tickets {filter !== "all" ? `with status "${filter.replace("_", " ")}"` : "yet"}.
              </td></tr>
            )}
            {rows.map((t) => (
              <tr key={t.id}
                  onClick={() => setOpenId(t.id)}
                  className="border-t border-border/60 hover:bg-muted/20 cursor-pointer"
                  data-testid={`ticket-row-${t.id}`}>
                <td className="px-3 py-2 font-medium max-w-xs truncate">{t.subject}</td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[t.status] || ""}`}>
                    {t.status.replace("_", " ")}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className={`text-[10px] ${PRIORITY_STYLES[t.priority] || ""}`}>
                    {t.priority}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{t.contact_name || "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{t.assignee_name || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  {t.sla_breached ? (
                    <span className="text-red-600 dark:text-red-400 inline-flex items-center gap-1">
                      <Flame className="w-3 h-3"/> breached
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{slaLabel(t.sla_due_in_minutes)}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {t.updated_at?.slice(0, 16).replace("T", " ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {creating && <CreateTicketDialog onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }}/>}
      {openId && <TicketDetailDialog ticketId={openId} onClose={() => setOpenId(null)} onChanged={load}/>}
    </>
  );
}

function slaLabel(mins) {
  if (mins == null) return "—";
  if (mins < 0) return "overdue";
  if (mins < 60) return `${mins}m left`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)}h left`;
  return `${Math.floor(mins / (60 * 24))}d left`;
}

function Tile({ label, value, icon: Icon, tone }) {
  const tones = {
    blue:    "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200/60",
    violet:  "bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-200/60",
    amber:   "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200/60",
    emerald: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200/60",
    red:     "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 border-red-200/60",
  };
  return (
    <Card className={`p-3 flex items-center gap-3 border ${tones[tone] || ""}`} data-testid={`ticket-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon className="w-5 h-5"/>
      <div>
        <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
        <div className="text-xl font-bold leading-tight">{value}</div>
      </div>
    </Card>
  );
}

function CreateTicketDialog({ onClose, onCreated }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("medium");
  const [contactName, setContactName] = useState("");
  const [busy, setBusy] = useState(false);
  async function save() {
    if (!subject.trim()) return;
    setBusy(true);
    try {
      await api.post("/tickets", { subject: subject.trim(), body, priority,
                                    contact_name: contactName || undefined });
      toast.success("Ticket created");
      onCreated();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ticket-create-dialog">
        <DialogHeader><DialogTitle>New ticket</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Cannot login to dashboard" data-testid="ticket-subject"/>
          </div>
          <div>
            <Label>Description</Label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)}
              rows={4} placeholder="Steps to reproduce, expected behaviour…"
              className="w-full text-sm rounded-md border border-input bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="ticket-body"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)}
                className="w-full text-sm rounded-md border border-input bg-background px-3 py-2"
                data-testid="ticket-priority">
                {TICKET_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <Label>Contact (optional)</Label>
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)}
                placeholder="Customer name" data-testid="ticket-contact"/>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !subject.trim()}
            className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="ticket-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TicketDetailDialog({ ticketId, onClose, onChanged }) {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);

  async function load() {
    try { const r = await api.get(`/tickets/${ticketId}`); setT(r.data); }
    catch (e) { toast.error(formatErr(e)); onClose(); }
  }
  useEffect(() => { load(); /* eslint-disable-line */ }, [ticketId]);

  async function patch(p) {
    setBusy(true);
    try {
      const r = await api.patch(`/tickets/${ticketId}`, p);
      setT(r.data);
      onChanged?.();
      toast.success("Ticket updated");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  }

  async function postComment() {
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await api.post(`/tickets/${ticketId}/comments`, { body: comment.trim() });
      setComment("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setPosting(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ticket-detail-dialog">
        <DialogHeader>
          <DialogTitle className="pr-6">{t ? t.subject : <Loader2 className="w-5 h-5 animate-spin"/>}</DialogTitle>
        </DialogHeader>
        {t && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <select value={t.status} disabled={busy}
                onChange={(e) => patch({ status: e.target.value })}
                className="text-xs rounded-md border border-input bg-background px-2 py-1"
                data-testid="ticket-status-select">
                {TICKET_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
              <select value={t.priority} disabled={busy}
                onChange={(e) => patch({ priority: e.target.value })}
                className="text-xs rounded-md border border-input bg-background px-2 py-1"
                data-testid="ticket-priority-select">
                {TICKET_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              {t.sla_breached && (
                <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200 gap-1">
                  <Flame className="w-3 h-3"/> SLA breached
                </Badge>
              )}
              <Badge variant="outline" className="text-[10px] ml-auto">
                opened by {t.created_by_name} · {t.created_at?.slice(0, 16).replace("T", " ")}
              </Badge>
            </div>

            {t.body && (
              <Card className="p-3 text-sm whitespace-pre-wrap bg-muted/30" data-testid="ticket-body-view">
                {t.body}
              </Card>
            )}

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-muted-foreground">Contact: </span>{t.contact_name || "—"}</div>
              <div><span className="text-muted-foreground">Assignee: </span>{t.assignee_name || "—"}</div>
              <div><span className="text-muted-foreground">Conversation: </span><span className="font-mono">{t.conversation_id?.slice(0, 12) || "—"}</span></div>
              <div><span className="text-muted-foreground">CDR: </span><span className="font-mono">{t.cdr_id?.slice(0, 12) || "—"}</span></div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                <MessageSquare className="w-3 h-3"/> Timeline ({(t.comments || []).length})
              </div>
              <div className="space-y-2" data-testid="ticket-comments">
                {(t.comments || []).map((c) => (
                  <div key={c.id} className={`p-2 rounded-md text-xs border ${c.kind === "system" ? "bg-muted/40 border-dashed text-muted-foreground italic" : "bg-card border-border"}`}>
                    <div className="flex justify-between gap-2 text-[10px] uppercase tracking-wider mb-1 opacity-70">
                      <span>{c.author_name}{c.kind === "system" ? " · system" : ""}</span>
                      <span>{c.created_at?.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="whitespace-pre-wrap">{c.body}</div>
                  </div>
                ))}
                {(t.comments || []).length === 0 && (
                  <div className="text-xs text-muted-foreground italic text-center py-4">No comments yet.</div>
                )}
              </div>
            </div>

            <div className="flex gap-2 sticky bottom-0 bg-background pt-2">
              <Input value={comment} onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && postComment()}
                placeholder="Add a reply or internal note…" data-testid="ticket-comment-input"/>
              <Button onClick={postComment} disabled={posting || !comment.trim()}
                className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"
                data-testid="ticket-comment-send">
                {posting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ===================== SYNC LOG TAB ===================== */
function SyncLogTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/integrations/crm/sync-log?limit=200").then((r) => setRows(r.data.rows || []));
  }, []);
  return (
    <Card className="overflow-hidden" data-testid="sync-log-table">
      <table className="w-full text-sm">
        <thead className="bg-muted/40">
          <tr>
            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">When</th>
            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Connector</th>
            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Kind</th>
            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Ref</th>
            <th className="text-left px-3 py-2 text-xs uppercase tracking-wider">Payload</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-muted-foreground italic">No sync activity yet.</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border/60">
              <td className="px-3 py-2 text-xs text-muted-foreground">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
              <td className="px-3 py-2 font-medium">{r.connector_id}</td>
              <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{r.kind}</Badge></td>
              <td className="px-3 py-2 font-mono text-xs">{r.ref_id?.slice(0, 12) || "—"}</td>
              <td className="px-3 py-2 text-xs text-muted-foreground">
                {Object.entries(r.payload_summary || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
